<template>
  <div class="read-results">
    <h3>検証結果</h3>
    <div class="summary">
      <p>
        項目数: <span>{{ totalItems }}</span>
      </p>
      <p class="error-count" :class="{ 'has-errors': errorCount }">
        エラー数: <span>{{ errorCount }}</span>
      </p>
    </div>
    <table v-if="isFreeTextError" class="error-table">
      <thead>
        <tr>
          <th>ページ</th>
          <th>表示順(@)</th>
          <th>項目名</th>
          <th>PDFリネーム(#)</th>
          <th>型(=)</th>
          <th>エンジン(*)</th>
          <th>エラー</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="({ contents, fieldName, page, displayOrder, itemName, pdfName, fieldType, engineName  }, index) in annotations.filter((a) => a.contents?.trim() !== '')"
          :key="index"
          :class="getRowClass(contents, page, fieldName)"
        >
          <td>{{ page }}</td>
          <td>{{ displayOrder }}</td>
          <td>{{ itemName || fieldName }}</td>
          <td>{{ pdfName }}</td>
          <td>{{ fieldType }}</td>
          <td>{{ engineName }}</td>
          <td>
            <span
              v-for="(error, errIndex) in validationErrors.filter((err) => (err.annotation === contents) && err.page === page)"
              :key="'error-cell-' + index + '-' + errIndex"
              :class="error.level === 'error' ? 'error-message' : 'warning-message'"
            >
              {{ error.message }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- form-field -->
    <table v-else class="error-table">
      <thead>
        <tr>
          <th>ページ</th>
          <th>項目名</th>
          <th>型(=)</th>
          <th>エラー</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="({ contents, fieldName, page, fieldType, checkBox, combo, radioButton  }, index) in annotations"
          :key="index"
          :class="getRowClass(contents, page, fieldName)"
        >
          <td>{{ page }}</td>
          <td>{{ fieldName }}</td>
          <td>{{ getFieldDisplayType(fieldType,checkBox,combo,radioButton) }}</td>
          <td>
            <span
              v-for="(error, errIndex) in validationErrors.filter((err) => (err.annotation === contents || err.annotation === fieldName) && err.page === page)"
              :key="'error-cell-' + index + '-' + errIndex"
              :class="error.level === 'error' ? 'error-message' : 'warning-message'"
            >
              {{ error.message }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ValidationError } from '@/lib/validateAnnotation';

export default defineComponent({
  name: 'ValidationAnnotationsTable',
  props: {
    validationErrors: {
      type: Array as () => ValidationError[],
      required: true,
    },
    annotations: {
      type: Array as () => any[],
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    errorCount: {
      type: Number,
      required: true,
    },
  },
 computed: {
  isFreeTextError(): boolean {
    if (!this.validationErrors.length) {
      return false;
    }

    for (const error of this.validationErrors) {
      const isFreeText = this.annotations.some(annotation =>
        annotation.contents && error.annotation === annotation.contents
      );

      if (!isFreeText) return false;
    }

    return true;
  }
  },
  methods: {
    getRowClass(contents: string, page: number, fieldName?:string) {
      const errors = this.validationErrors.filter((err) => (err.annotation === contents || err.annotation === fieldName) &&
      err.page === page);
      if (errors.some((e) => e.level === 'error')) return 'error-row';
      if (errors.some((e) => e.level === 'warning')) return 'warning-row';
      return '';
    },
    getFieldDisplayType(fieldType: string, checkBox?: boolean, radioButton?: boolean, combo?: boolean): string {
      switch (fieldType) {
        case 'Tx':
          return 'Input'; 
        case 'Btn':
          if (checkBox) return 'checkbox';
          if (radioButton) return 'radio';
          return 'ボタン';
        case 'Ch':
          if (combo) return 'dropdown';
          return 'リストボックス';
        default:
          return '不明';
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;

.read-results {
  margin-top: 30px;
  border: 1px solid var(--bs-gray-300);
  border-radius: 8px;
  padding: 15px;
  background-color: var(--bs-light);
  overflow-x: auto;

  h3 {
    color: var(--bs-dark);
    margin-bottom: 20px;
    font-size: 1.5rem;
    text-align: center;
  }

  .summary {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 10px 0;
    margin-bottom: 20px;
    background-color: var(--bs-white);
    border-radius: 5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    p {
      margin: 0;
      font-size: 1.1rem;
      color: var(--bs-dark);

      span {
        font-weight: bold;
        color: var(--bs-primary);
      }

      &.error-count {
        span {
          color: var(--bs-success);
        }

        &.has-errors {
          span {
            color: var(--bs-danger);
            animation: pulse-red 1s infinite;
          }
        }
      }
    }
  }

  .error-table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;

    thead {
      tr {
        background-color: var(--bs-dark);
        color: var(--bs-white);
      }
    }

    th,
    td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid var(--bs-gray-200);
      vertical-align: top;
      word-wrap: break-word;
      max-width: 200px;
    }

    th {
      font-weight: 600;
      font-size: 0.95rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    tbody {
      tr:nth-child(even) {
        background-color: var(--bs-gray-100);
      }

      tr:hover {
        background-color: var(--bs-primary-light);
        transition: background-color 0.3s ease;
      }

      tr.error-row {
        background-color: rgba(var(--bs-danger-rgb), 0.1);
        color: var(--bs-danger);
        font-weight: 500;
        animation: fadeInError 0.5s ease-out;
        border-left: 4px solid var(--bs-danger);

        td {
          border-bottom-color: var(--bs-danger-dark);
        }

        &:hover {
          background-color: rgba(var(--bs-danger-rgb), 0.2);
        }
      }

      tr.warning-row {
        background-color: rgba(var(--bs-warning-rgb), 0.1);
        color: var(--bs-warning-dark);
        font-weight: 500;
        border-left: 4px solid var(--bs-warning);

        &:hover {
          background-color: rgba(var(--bs-warning-rgb), 0.2);
        }
      }
    }

    .error-message {
      display: block;
      color: var(--bs-danger);
      font-size: 0.85rem;
      font-weight: 500;
      margin-top: 5px;
      padding-left: 5px;
      border-left: 3px solid var(--bs-danger);
      white-space: normal;
    }

    .warning-message {
      display: block;
      color: var(--bs-warning-dark);
      font-size: 0.85rem;
      font-weight: 500;
      margin-top: 5px;
      padding-left: 5px;
      border-left: 3px solid var(--bs-warning);
      white-space: normal;
    }
  }
}

@keyframes pulse-red {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInError {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
